import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Button, Badge, PillTab, SEO } from '../../../components/common';
import colors from '../../../styles/colors';
import { shoppingService } from '../../../api/services';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useMoney } from '../../../hooks/useMoney';
import { translateCategoryName, translateProductName } from '../../../constants/shoppingI18n';

export default function Shopping() {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const beLang = i18n.language || 'ko';
  const navigate = useNavigate();
  // #103: 쇼핑 페이지의 예약하기는 출발지→공항 모드로 진입
  const goAirportToAirport = () => navigate('/transfer/airport', { state: { formData: { direction: 'to-airport' } } });

  // #100: 3개 step 카드를 색상으로 차별화 (각 단계별 그라디언트)
  const steps = [
    {
      number: '01',
      icon: 'airport',
      title: t('info.shopping_step1_title'),
      description: t('info.shopping_step1_desc'),
      bgClass: 'bg-gradient-to-br from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200',
    },
    {
      number: '02',
      icon: 'shopping',
      title: t('info.shopping_step2_title'),
      description: t('info.shopping_step2_desc'),
      bgClass: 'bg-gradient-to-br from-orange-100 to-orange-200 hover:from-orange-200 hover:to-orange-300',
    },
    {
      number: '03',
      icon: 'van',
      title: t('info.shopping_step3_title'),
      description: t('info.shopping_step3_desc'),
      highlighted: true,
      // #261: 3단계 주황 과강조 완화 — 채도↓·밝기↑(from-200/to-400 → from-100/to-300)로 회색 설명 가독성 개선.
      bgClass: 'bg-gradient-to-br from-orange-100 to-orange-300 hover:from-orange-200 hover:to-orange-400',
    },
  ];

  const [activeCategory, setActiveCategory] = useState('all');
  const [categoryTabs, setCategoryTabs] = useState([{ id: 'all', label: t('info.shopping_all_category') }]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    shoppingService.getRetailCategories().then((cats) => {
      setCategoryTabs([{ id: 'all', label: t('info.shopping_all_category') }, ...cats.map((c) => ({ id: c.id, label: c.label }))]);
    }).catch(() => { /* 카테고리 탭 — 보조 데이터 */ });
    shoppingService.getRetailProducts().then((data) => {
      setProducts(data.map((p) => ({
        id: p.id,
        name: p.name,
        brand: p.category || t('info.shopping_default_category'),
        category: p.category,
        // 원본 KRW 가격 보존 — 렌더 시점에 useMoney 로 통화 변환 (UI 언어 변경 시 즉시 재렌더).
        priceAmount: Number(p.price) || 0,
        image: p.image_url,
      })));
    }).catch(() => {});
  }, [t]);

  const filteredProducts = activeCategory === 'all' ? products : products.filter((p) => p.category === activeCategory);

  // 카테고리 탭 라벨 + 상품명 — 정적 매핑(shoppingI18n) 우선, 미매핑만 DeepL fallback
  const catLabelsBundle = useMemo(
    // 정적 매핑이 있으면 이미 번역된 값이 들어감 → DeepL은 이 값을 한국어 감지 못하므로 skip
    () => categoryTabs.map((c) => translateCategoryName(c.label || '', beLang)),
    [categoryTabs, beLang]
  );
  const tCatLabels = useDeepLTranslated(catLabelsBundle);
  const tCategoryTabs = categoryTabs.map((c, i) => ({ ...c, label: tCatLabels[i] || c.label }));

  const productTextsBundle = useMemo(() => {
    const out = [];
    filteredProducts.forEach((p) => {
      out.push(translateProductName(p.name || '', beLang), translateCategoryName(p.brand || '', beLang));
    });
    return out;
  }, [filteredProducts, beLang]);
  const tProductTexts = useDeepLTranslated(productTextsBundle);
  const tFilteredProducts = filteredProducts.map((p, i) => ({
    ...p,
    name: tProductTexts[i * 2] || p.name,
    brand: tProductTexts[i * 2 + 1] || p.brand,
    price: money(p.priceAmount),
  }));

  return (
    <div className="bg-white min-h-screen pb-28">
      <SEO serviceType="Private Shopping Tour" />
      {/* Hero Section */}
      <div className="bg-orange-50 py-10 sm:py-16">
        <div className="max-w-content mx-auto px-2.5 sm:px-5 text-center">
          {/* Label */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <Icon name="shopping" size={18} className="text-orange-500" />
            <span className="text-sm font-bold text-orange-500">Private Shopping</span>
          </div>

          {/* Title - "쇼핑" with highlight marker effect */}
          <h1 className="text-xl sm:text-[32px] font-bold text-mono-900 mb-4 leading-snug break-keep">
            {t('info.shopping_hero_title_1')}
            <br />
            <span
              className="text-orange-500"
              style={{ background: `linear-gradient(to top, ${colors.orange[100]} 35%, transparent 35%)` }}
            >
              {t('info.shopping_hero_title_2')}
            </span>
            {t('info.shopping_hero_title_3')}
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base font-medium text-mono-500 max-w-narrow mx-auto leading-relaxed">
            {t('info.shopping_hero_desc1')}<br />
            {t('info.shopping_hero_desc2')}
          </p>

          {/* 3일 전 예약 안내 — strong 태그 포함 i18n value를 안전하게 렌더 (값은 우리가 통제하는 정적 i18n) */}
          <p
            className="mt-4 text-sm font-medium text-orange-500 break-keep"
            dangerouslySetInnerHTML={{ __html: t('info.shopping_3day_notice') }}
          />

          {/* 3-Step Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-8 sm:mt-10 max-w-modal-md mx-auto">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`rounded-xl px-2 py-3 sm:p-5 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg cursor-default ${step.bgClass}`}
              >
                <p className={`text-base sm:text-xl font-bold mb-1.5 sm:mb-3 ${
                  step.highlighted ? 'text-orange-500' : 'text-orange-400'
                }`}>
                  {step.number}
                </p>
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mx-auto mb-1.5 sm:mb-3 ${
                    step.highlighted ? 'bg-white/60' : 'bg-orange-50'
                  }`}
                >
                  <Icon name={step.icon} size={18} className="text-orange-500 sm:hidden" />
                  <Icon name={step.icon} size={24} className="text-orange-500 hidden sm:block" />
                </div>
                <h3 className="text-2sm sm:text-md font-bold text-mono-900 mb-1 min-h-[40px] sm:min-h-[46px] whitespace-pre-line leading-tight flex items-center justify-center">
                  {step.title}
                </h3>
                <p className="text-[13px] text-mono-600 leading-normal">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Product Catalog - centered */}
      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-10 sm:py-16">
        <div className="mb-8 text-center">
          <p className="text-sm font-bold text-orange-500 mb-2">{t('info.shopping_catalog_label')}</p>
          <h2 className="text-xl font-bold text-mono-900">{t('info.shopping_catalog_title')}</h2>
        </div>

        {/* Category Pill Tabs - centered */}
        <PillTab
          tabs={tCategoryTabs}
          activeTab={activeCategory}
          onTabChange={setActiveCategory}
          className="mb-6 justify-center"
        />

        {/* Product Grid */}
        {filteredProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Icon name="shopping" size={48} className="text-mono-200" />
            <p className="text-base font-bold text-mono-400">{t('info.shopping_no_products_title')}</p>
            <p className="text-sm text-mono-300">{t('info.shopping_no_products_desc')}</p>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {tFilteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl overflow-hidden border border-mono-100 hover:shadow-card transition flex flex-col"
            >
              {/* BugHerd #50: 어드민 저장 이미지 표시 — image_url 있으면 렌더, 없으면 placeholder */}
              <div className="aspect-square bg-mono-100 flex items-center justify-center overflow-hidden">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" class="text-mono-400"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"></path></svg>'; }}
                  />
                ) : (
                  <Icon name="image" size={32} className="text-mono-400" />
                )}
              </div>
              {/* flex-1 + flex-col: 카드 높이 풀로 채워서 행 정렬 / mt-auto on price: 상품명 길이 무관 가격 하단 고정.
                 space-y-* 는 ':not([hidden]) ~ :not([hidden])' 셀렉터로 mt-auto 보다 특이도가 높아 충돌 → gap-1 사용. */}
              <div className="p-3 flex flex-col flex-1 gap-1">
                <Badge variant="shopping">{product.brand}</Badge>
                <h3 className="text-[17px] font-bold text-mono-900">{product.name}</h3>
                <p className="text-[15px] font-normal text-mono-900 mt-auto">{product.price}{/^US\$/.test(String(product.price)) ? ' ~' : ''}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Banner - Gradient Box */}
      <div className="px-2.5 sm:px-5 pb-10 sm:pb-16">
        <div className="max-w-[1000px] mx-auto bg-gradient-to-r from-orange-700 to-orange-500 rounded-xl p-5 sm:py-5 sm:px-10 flex flex-col sm:flex-row items-center sm:justify-between gap-4">
          <div className="text-center sm:text-left space-y-1.5">
            <p className="text-[13px] sm:text-base font-bold text-orange-200">
              {t('info.shopping_cta_label')}
            </p>
            <p className="text-lg sm:text-2xl font-bold text-white break-keep whitespace-pre-line">
              {t('info.shopping_cta_title')}
            </p>
            <p className="text-[13px] sm:text-base font-bold text-white">
              {t('info.shopping_cta_desc')}
            </p>
          </div>
          <Button level="inverse" width="fixed-140" className="shrink-0" onClick={goAirportToAirport}>
            {t('info.shopping_cta_button')}
          </Button>
        </div>
      </div>

      {/* Floating Bottom Bar */}
      {/* #264: 공용 마커 부여 → FloatingButtons(채팅·TOP)가 이 바를 감지해 위로(bottom-24) 올라가 겹침 방지. */}
      <div
        data-sticky-bottom-bar
        className="fixed bottom-0 left-0 right-0 z-30"
        style={{ filter: 'drop-shadow(0px -4px 30px rgba(0, 0, 0, 0.1))' }}
      >
        <div className="bg-white pb-safe">
          <div className="max-w-content mx-auto px-2.5 sm:px-5 py-3 sm:py-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="hidden sm:flex w-12 h-12 bg-orange-50 rounded-xl items-center justify-center shrink-0">
                <Icon name="shopping" size={28} className="text-orange-500" />
              </div>
              <span className="text-[13px] sm:text-[15px] font-bold text-mono-900 break-keep">
                {t('info.shopping_floating')}
              </span>
            </div>
            <Button level="level1" width="responsive-140" className="shrink-0" onClick={goAirportToAirport}>{t('common.book_now')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
